import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Heart,
  Laptop,
  Globe,
  GraduationCap,
  Users,
  Zap,
  ArrowRight,
  MapPin,
  Clock,
  Briefcase,
  LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContentHeader, ContentSection } from "@/components/content";

export const Route = createFileRoute('/info/careers')({
  component: CareersPage,
  head: () => ({
    meta: [
      { title: 'Careers | Matricks' },
      { name: 'description', content: 'Join the Matricks team. We\'re building the future of enterprise integration and AI agent platforms.' },
    ],
  }),
});

const iconMap: Record<string, LucideIcon> = {
  Heart,
  Laptop,
  Globe,
  GraduationCap,
  Users,
  Zap,
};

const benefits = [
  {
    iconName: "Heart",
    title: "Health & Wellness",
    description:
      "Comprehensive health, dental, and vision insurance. Plus mental health support and wellness stipend.",
  },
  {
    iconName: "Laptop",
    title: "Remote-First",
    description:
      "Work from anywhere in the world. We believe in hiring the best talent, regardless of location.",
  },
  {
    iconName: "Globe",
    title: "Flexible Hours",
    description:
      "Set your own schedule. We focus on results, not hours logged. Work when you're most productive.",
  },
  {
    iconName: "GraduationCap",
    title: "Learning & Growth",
    description:
      "Annual learning budget for courses, conferences, and books. We invest in your professional development.",
  },
  {
    iconName: "Users",
    title: "Team Events",
    description:
      "Regular team retreats, virtual events, and collaboration opportunities to build strong connections.",
  },
  {
    iconName: "Zap",
    title: "Equity",
    description:
      "Competitive salary plus equity package. Share in the success of what we build together.",
  },
];

const openPositions = [
  {
    title: "Senior Backend Engineer",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    description:
      "Build and scale our API gateway and integration platform. Experience with Node.js, PostgreSQL, and distributed systems required.",
  },
  {
    title: "Senior Frontend Engineer",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    description:
      "Create beautiful, performant user interfaces for our platform. Experience with React, TypeScript, and design systems required.",
  },
  {
    title: "AI/ML Engineer",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    description:
      "Design and implement AI agent orchestration systems. Experience with LLMs, prompt engineering, and agent frameworks required.",
  },
  {
    title: "Product Designer",
    department: "Design",
    location: "Remote",
    type: "Full-time",
    description:
      "Shape the user experience of our platform. Experience with enterprise SaaS, design systems, and user research required.",
  },
  {
    title: "Developer Advocate",
    department: "Developer Relations",
    location: "Remote",
    type: "Full-time",
    description:
      "Help developers succeed with Matricks. Experience with technical writing, public speaking, and community building required.",
  },
  {
    title: "Enterprise Account Executive",
    department: "Sales",
    location: "Remote (US)",
    type: "Full-time",
    description:
      "Drive enterprise sales and build relationships with Fortune 500 companies. Experience with enterprise SaaS sales required.",
  },
];

function CareersPage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        {/* Header */}
        <ContentHeader
          title="Build the future with us"
          description="We're looking for passionate people to join our mission of making enterprise integration accessible to everyone."
          centered
        />

        {/* Culture Section */}
        <div className="mt-20 rounded-2xl bg-muted/50 p-8 sm:p-12">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold">Our Culture</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              At Matricks, we believe in building a company where talented people can do their best work.
              We're a remote-first team that values transparency, autonomy, and impact. We move fast,
              take ownership, and celebrate wins together.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We're building something special, and we want you to be part of it. Whether you're an
              experienced professional or just starting your career, if you're passionate about
              technology and want to make a difference, we'd love to hear from you.
            </p>
          </div>
        </div>

        {/* Benefits */}
        <ContentSection
          title="Why join Matricks?"
          description="We take care of our team so they can focus on doing great work."
          className="mt-24"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="flex gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                  {(() => {
                    const Icon = iconMap[benefit.iconName] || Heart;
                    return <Icon className="h-5 w-5 text-primary" />;
                  })()}
                </div>
                <div>
                  <h3 className="font-semibold">{benefit.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </ContentSection>

        {/* Open Positions */}
        <ContentSection
          title="Open Positions"
          description="Find your next opportunity. We're hiring across all departments."
          className="mt-24"
        >
          <div className="space-y-4">
            {openPositions.map((position) => (
              <div
                key={position.title}
                className="rounded-xl border bg-card p-6 transition-all hover:shadow-md hover:border-primary/50"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold">{position.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{position.description}</p>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {position.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {position.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {position.type}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" className="flex-shrink-0" asChild>
                    <Link to={`/info/careers` as any}>
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </ContentSection>

        {/* Don't see a role */}
        <div className="mt-24 rounded-2xl bg-primary/5 p-8 sm:p-12 text-center">
          <h2 className="text-2xl font-bold">Don't see the right role?</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            We're always looking for talented people to join our team. If you think you'd be a great
            fit for Matricks, we'd love to hear from you. Send us your resume and tell us why you'd
            like to join.
          </p>
          <Button className="mt-8" size="lg" asChild>
            <a href="mailto:careers@matricks.ai">
              Send Your Resume
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
